import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import axios from "axios";
import { Button } from "@chakra-ui/react";
import { FiSend } from "react-icons/fi";
import DisplayAmountWithDecimals from "../../shared/DisplayAmountWithDecimals";
import { ImSpinner8 } from "react-icons/im";
import { ProfileContext } from "../../../../store/ProfileProvider";
import { GrTransaction } from "react-icons/gr";
import AcceptOfferConfirmationModal from "./AcceptOfferConfirmationModal";
import CloseTicketConfirmationModal from "./CloseTicketConfirmationModal";
import SubmitOfferModal from "./SubmitOfferModal";
import { SubaccountContext } from "../../../../store/SubaccountProvider";

interface PayloadType {
  [key: string]: any;
}

type Message = {
  id: number;
  text: string;
  senderRole: string;
  date: number;
  messageType: string;
  data?: any;
};

type Conversation = {
  text: string;
  senderRole: string;
  date: number;
  messageType: string;
  data: any;
};

type TicketChatBoxProps = {
  tradeDetail: any;
  setTradeDetail: any;
  setSwalProps: any;
  latestOfferDetail: any;
  setLatestOfferDetail: any;
  isChatLoading: boolean;
  setIsChatLoading: (loading: boolean) => void;
};

type CountdownTimerProps = {
  expiryTimestamp: number;
  onExpire: (offerId: number) => void;
  offerId: number;
};

const WS_ENDPOINT = process.env.REACT_APP_WS_URL!;
const TICKET_MESSAGE_PER_PAGE = Number(process.env.REACT_APP_TICKET_MESSAGE_PER_PAGE!) || 20;

const TicketChatBox: React.FC<TicketChatBoxProps> = ({
  tradeDetail,
  setTradeDetail,
  setSwalProps,
  latestOfferDetail,
  setLatestOfferDetail,
  isChatLoading,
  setIsChatLoading
}) => {
  const {
    setSubaccountBalance,
    setIsGettingBalance,
  } = useContext(SubaccountContext);
  const socketRef = useRef<WebSocket | null>(null);
  const { profileTimeZone } = useContext(ProfileContext);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  // WebSocket connection state
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);

  // Keep-alive references
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatInterval = 5 * 60 * 1000; // 5 minutes

  // Lazy loading states
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const [displayedMessages, setDisplayedMessages] = useState<Message[]>([]);
  const [messagesPerLoad] = useState<number>(TICKET_MESSAGE_PER_PAGE);

  const [newMessage, setNewMessage] = useState<string>("");
  const [sendingMessage, setSendingMessage] = useState<boolean>(false);

  const [isOpenConfirmOfferModal, setIsOpenConfirmOfferModal] = useState<boolean>(false);
  const [isOpenAcceptOfferConfirmationModal, setIsOpenAcceptOfferConfirmationModal] = useState<boolean>(false);
  const [isOpenConfirmCloseTicketModal, setIsOpenConfirmCloseTicketModal] = useState<boolean>(false);
  const [selectedOfferDetail, setSelectedOfferDetail] = useState<any>(null);

  const userId = localStorage.getItem("userid");
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const stableTradeDetail = useMemo(() => {
    if (!tradeDetail) return null;

    return {
      id: tradeDetail.id,
      shortid: tradeDetail.shortid,
      ticket_id: tradeDetail.ticket_id,
      from_asset: tradeDetail.from_asset,
      to_asset: tradeDetail.to_asset,
      from_amount: tradeDetail.from_amount,
      offer_history: tradeDetail.offer_history,
      to_address: tradeDetail.to_address,
      subaccount: tradeDetail.subaccount,
    };
  }, [
    tradeDetail?.id,
    tradeDetail?.shortid,
    tradeDetail?.ticket_id,
    tradeDetail?.from_asset,
    tradeDetail?.to_asset,
    tradeDetail?.from_amount,
    tradeDetail?.to_address,
    tradeDetail?.subaccount
  ]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const isUserNearBottom = () => {
    if (!chatContainerRef.current) return false;
    const container = chatContainerRef.current;
    const threshold = 100;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
    return isNearBottom;
  };

  const hasMoreOlderMessages = () => {
    return displayedMessages.length < allMessages.length;
  };

  // Keep-alive functions
  const startHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    heartbeatIntervalRef.current = setInterval(() => {
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        try {
          const pingMessage = {
            action: "ping",
            timestamp: Date.now()
          };
          socketRef.current.send(JSON.stringify(pingMessage));
          console.log("Keep-alive ping sent");
        } catch (error) {
          console.error("Keep-alive ping failed:", error);
        }
      }
    }, heartbeatInterval);
  };

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  // Join socket room with keep-alive
  const joinSocketRoom = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        // Stop existing heartbeat
        stopHeartbeat();

        // Close existing connection if any
        if (socketRef.current) {
          socketRef.current.close();
          socketRef.current = null;
        }

        const apiToken = localStorage.getItem("apiToken");
        const wsUrlWithToken = `${WS_ENDPOINT}?authorization=${encodeURIComponent(`Bearer ${apiToken}`)}`;
        const socket = new WebSocket(wsUrlWithToken);
        socketRef.current = socket;

        const connectionTimeout = setTimeout(() => {
          socket.close();
          reject(new Error('WebSocket connection timeout'));
        }, 10000);

        socket.onopen = () => {
          clearTimeout(connectionTimeout);
          console.log("WebSocket connected");
          setIsReconnecting(false);

          const joinPayload = {
            action: "joinRoom",
            roomId: tradeDetail.shortid,
            userId,
            userRole,
          };
          socket.send(JSON.stringify(joinPayload));

          // Start keep-alive pings every 5 minutes
          startHeartbeat();

          resolve();
        };

        socket.onmessage = async (event) => {
          const message = JSON.parse(event.data);
          console.log('onmessage: ', message);

          // Handle ping/pong responses (ignore them)
          if (message.action === "pong" || message.action === "ping") {
            return;
          }

          if (message.type === "stream-message" && message.roomId === tradeDetail.shortid) {
            const messageData: Message = {
              id: message.id,
              text: message.message,
              senderRole: message.senderRole,
              date: message.date,
              messageType: message.messageType
            };

            const shouldAutoScroll = isUserNearBottom();

            if (message.messageType === "offer") {
              if (message?.data && "offerDetails" in message?.data && message?.data?.offerDetails) {
                messageData.data = {
                  offerDetails: {
                    trade_shortid: message?.data?.offerDetails.trade_shortid,
                    offer_id: message?.data?.offerDetails.offer_id,
                    sell: message?.data?.offerDetails.sell,
                    sell_asset: message?.data?.offerDetails.sell_asset,
                    receive: message?.data?.offerDetails.receive,
                    receive_asset: message?.data?.offerDetails.receive_asset,
                    rate: message?.data?.offerDetails.rate,
                    settlement_address: tradeDetail?.to_address?.address,
                    status: message?.data?.offerDetails.status,
                    expired_text: message?.data?.offerDetails?.expired_text,
                    expired: message?.data?.offerDetails?.expired
                  }
                };
              }

              setLatestOfferDetail(messageData.data.offerDetails);
              setAllMessages(prev => [...prev, messageData]);
              setDisplayedMessages((prev) => [
                ...prev.map(msg =>
                  message?.data?.offerDetails?.expired_text &&
                    msg.messageType === "offer" &&
                    msg.data?.offerDetails?.status === "pending"
                    ? {
                      ...msg,
                      data: {
                        ...msg.data,
                        offerDetails: {
                          ...msg.data.offerDetails,
                          status: "cancelled"
                        }
                      }
                    }
                    : msg
                ),
                messageData
              ]);
            } else if (message.messageType === "accept-offer") {
              const offerId = message?.data?.offerId;
              setTradeDetail((prev: any) => {
                if (prev.status !== "pending") {
                  return { ...prev, status: "pending" };
                }
                return prev;
              });

              setLatestOfferDetail((prev: any) => ({ ...prev, status: "accepted" }));

              const updateMessages = (prev: Message[]) =>
                prev.map(msg =>
                  msg.data?.offerDetails?.offer_id === offerId
                    ? {
                      ...msg,
                      messageType: "offer",
                      data: {
                        ...msg.data,
                        offerDetails: {
                          ...msg.data?.offerDetails,
                          status: "accepted",
                        }
                      }
                    }
                    : msg
                );

              setAllMessages(updateMessages);
              setDisplayedMessages(updateMessages);

            } else if (message.messageType === "cancel-offer") {
              const offerId = message?.data?.offerId;
              setLatestOfferDetail((prev: any) => ({ ...prev, status: "cancelled" }));
              const updateMessages = (prev: Message[]) =>
                prev.map(msg =>
                  msg.data?.offerDetails?.offer_id === offerId
                    ? {
                      ...msg,
                      messageType: "offer",
                      data: {
                        ...msg.data,
                        offerDetails: {
                          ...msg.data?.offerDetails,
                          status: "cancelled",
                        }
                      }
                    }
                    : msg
                );

              setAllMessages(updateMessages);
              setDisplayedMessages(updateMessages);
            } else if (message.messageType?.startsWith("trade-")) {
              // Handle trade status messages (trade-completed, trade-rejected, trade-cancelled)
              if (message?.data?.status) {
                // Update trade detail status
                setTradeDetail((prev: any) => ({
                  ...prev,
                  status: message.data.status,
                  ...(message.data.transaction_reference && { transaction_reference: message.data.transaction_reference }),
                  ...(message.data.reason && { reason: message.data.reason })
                }));
              }

              // Add the additional data to the message
              if (message?.data) {
                messageData.data = {
                  trade_id: message.data.trade_id,
                  status: message.data.status,
                  ...(message.data.transaction_reference && { transaction_reference: message.data.transaction_reference }),
                  ...(message.data.reason && { reason: message.data.reason })
                };
              }

              if (userRole !== "superadmin" && message.messageType === "trade-rejected") {
                setIsGettingBalance(true);
                await getSubaccountBalances();
                setIsGettingBalance(false);
              }

              if (message.messageType === "trade-cancelled") {
                const updateMessages = (prev: Message[]) => [
                  ...prev.map(msg => {
                    if (msg.messageType === "offer" &&
                      msg.data?.offerDetails?.status === "pending") {
                      return {
                        ...msg,
                        data: {
                          ...msg.data,
                          offerDetails: {
                            ...msg.data.offerDetails,
                            status: "cancelled"
                          }
                        }
                      };
                    }
                    return msg;
                  }),
                  messageData
                ];

                setAllMessages(updateMessages);
                setDisplayedMessages(updateMessages);
                setLatestOfferDetail((prev: any) => {
                  if (prev && prev.status === "pending") {
                    return { ...prev, status: "cancelled" };
                  }
                  return prev;
                });
              } else {
                setAllMessages(prev => [...prev, messageData]);
                setDisplayedMessages((prev) => [...prev, messageData]);
              }
            } else {
              setAllMessages(prev => [...prev, messageData]);
              setDisplayedMessages((prev) => [...prev, messageData]);
            }

            if (shouldAutoScroll) {
              setTimeout(() => {
                scrollToBottom();
              }, 100);
            }
          }
        };

        socket.onclose = (event) => {
          clearTimeout(connectionTimeout);
          console.log("WebSocket disconnected", event.code, event.reason);
          stopHeartbeat();
        };

        socket.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error("WebSocket error:", error);
          stopHeartbeat();
          reject(error);
        };

      } catch (error) {
        reject(error);
      }
    });
  };

  // Enhanced sendMessage - reconnect only when user tries to send
  const sendMessage = async (customMessage: any = null): Promise<void> => {
    try {
      setSendingMessage(true);

      // Only reconnect if connection is not open and user is trying to send
      if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
        setIsReconnecting(true);
        console.log("WebSocket not connected, reconnecting...");
        await joinSocketRoom();
      }

      // Send the message
      if (customMessage) {
        socketRef.current?.send(JSON.stringify(customMessage));
      } else {
        if (!newMessage.trim()) return;

        socketRef.current?.send(
          JSON.stringify({
            action: "sendMessage",
            messageType: "common",
            roomId: tradeDetail.shortid,
            message: newMessage,
            senderRole: userRole,
            ticketId: tradeDetail.ticket_id
          })
        );
      }

      setNewMessage("");
      setSendingMessage(false);

      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
      setSendingMessage(false);
      setIsReconnecting(false);
      console.error("Failed to send message:", error);
    }
  };

  // ✅ FIXED: Use stableTradeDetail to prevent unnecessary reinitialization
  useEffect(() => {
    const init = async () => {
      try {
        setAllMessages([]);
        setDisplayedMessages([]);
        setLatestOfferDetail(null);

        await joinSocketRoom();
        await fetchConversationHistory(stableTradeDetail.ticket_id);

        if (stableTradeDetail.offer_history && Array.isArray(stableTradeDetail.offer_history) && stableTradeDetail.offer_history.length > 0) {
          try {
            const currentOffer = stableTradeDetail.offer_history.reduce((latest: any, current: any) => {
              if (!latest?.offer_id || !current?.offer_id) {
                return latest || current;
              }

              const latestTimestamp = Number(latest.offer_id);
              const currentTimestamp = Number(current.offer_id);

              if (isNaN(latestTimestamp) || isNaN(currentTimestamp)) {
                return latest;
              }

              return currentTimestamp > latestTimestamp ? current : latest;
            });

            if (currentOffer) {
              setLatestOfferDetail(currentOffer);
            }
          } catch (error) {
            console.error('Error processing offer history:', error);
          }
        }

        setIsChatLoading(false);
        setTimeout(() => {
          scrollToBottom();
        }, 50);
      } catch (error) {
        console.error("Error initiate chat: ", error);
        setIsChatLoading(false);
      }
    };

    if (stableTradeDetail && stableTradeDetail.shortid) {
      init();
    }

    return () => {
      stopHeartbeat(); // Stop keep-alive

      if (socketRef.current) {
        socketRef.current.close(1000, "Component unmounting");
        socketRef.current = null;
      }
      setIsReconnecting(false);
    };
  }, [stableTradeDetail, setLatestOfferDetail]); // eslint-disable-line

  const fetchConversationHistory = async (ticket_id: string | number) => {
    try {
      let requestUrl = `/api/trade/trade-ticket-conversations-by-id?ticket_id=${ticket_id}`;
      const response = await axios.get(process.env.REACT_APP_API_URL + requestUrl);

      if (response.data.conversations) {
        const conversationsRebuild = response.data.conversations.map((conversation: Conversation) => {
          if (conversation.messageType === "offer" && conversation.data && conversation.data.offerId && tradeDetail.offer_history && tradeDetail.offer_history.length > 0) {
            const offerDetails = tradeDetail.offer_history.find(
              (offer: any) => {
                return offer.offer_id === conversation.data.offerId;
              }
            );
            if (offerDetails) {
              return {
                ...conversation,
                data: {
                  offerDetails: {
                    trade_shortid: tradeDetail.shortid,
                    offer_id: offerDetails.offer_id,
                    sell: offerDetails.sell,
                    sell_asset: offerDetails.sell_asset,
                    receive: offerDetails.receive,
                    receive_asset: offerDetails.receive_asset,
                    rate: offerDetails.rate,
                    settlement_address: tradeDetail?.to_address?.address,
                    status: offerDetails.status,
                    expired_text: offerDetails.expired_text,
                    expired: offerDetails.expired
                  }
                }
              };
            }
          }

          delete conversation.messageType;
          conversation.messageType = "common";
          return conversation;
        });

        const sortedMessages = conversationsRebuild.reverse();
        setAllMessages(sortedMessages);

        const totalMessages = sortedMessages.length;
        const startIndex = Math.max(0, totalMessages - messagesPerLoad);
        setDisplayedMessages(sortedMessages.slice(startIndex));
      } else {
        setAllMessages([]);
        setDisplayedMessages([]);
      }
    } catch (error) {
      console.error("Error fetching trades", error);
      setAllMessages([]);
      setDisplayedMessages([]);
    }
    setIsChatLoading(false);
  };

  const loadMoreMessages = () => {
    if (isLoadingMore || !hasMoreOlderMessages()) return;

    setIsLoadingMore(true);

    const container = chatContainerRef.current;
    const scrollHeightBefore = container ? container.scrollHeight : 0;
    const scrollTopBefore = container ? container.scrollTop : 0;

    setTimeout(() => {
      const currentDisplayedCount = displayedMessages.length;
      const totalMessages = allMessages.length;

      const remainingMessages = totalMessages - currentDisplayedCount;
      const messagesToLoad = Math.min(messagesPerLoad, remainingMessages);

      const newStartIndex = Math.max(0, totalMessages - currentDisplayedCount - messagesToLoad);
      const newEndIndex = totalMessages - currentDisplayedCount;

      const additionalMessages = allMessages.slice(newStartIndex, newEndIndex);

      setDisplayedMessages(prev => [...additionalMessages, ...prev]);
      setIsLoadingMore(false);

      setTimeout(() => {
        if (container) {
          const scrollHeightAfter = container.scrollHeight;
          const heightDifference = scrollHeightAfter - scrollHeightBefore;
          container.scrollTop = scrollTopBefore + heightDifference;
        }
      }, 50);
    }, 1000);
  };

  const formatTime = (timestamp: number, timezone: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone
    });
  };

  const formatDateHeader = (timestamp: number, timezone: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      const options: Intl.DateTimeFormatOptions = {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: timezone,
      };

      const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(date);

      const weekday = parts.find((p) => p.type === "weekday")?.value;
      const month = parts.find((p) => p.type === "month")?.value;
      const day = parts.find((p) => p.type === "day")?.value;
      const year = parts.find((p) => p.type === "year")?.value;

      return `${weekday}, ${month} ${day} ${year}`;
    }
  };

  const shouldShowDateHeader = (currentMsg: any, previousMsg: any, timezone: string) => {
    if (!previousMsg) return true;

    const currentDate = new Date(currentMsg.date).toDateString();
    const previousDate = new Date(previousMsg.date).toDateString();

    return currentDate !== previousDate;
  };

  const CountdownTimer: React.FC<CountdownTimerProps> = ({ expiryTimestamp, onExpire, offerId }) => {
    const [timeLeft, setTimeLeft] = useState('');
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
      const updateCountdown = () => {
        const now = Date.now();
        const timeRemaining = expiryTimestamp - now;

        if (timeRemaining <= 0) {
          setTimeLeft('0h:0m:0s');
          setIsExpired(true);
          onExpire(offerId);
          return;
        }

        const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
        const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

        setTimeLeft(`${hours}h:${minutes}m:${seconds}s`);
      };

      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }, [expiryTimestamp, onExpire, offerId]);

    return (
      <span className={`text-sm font-mono text-right break-all ${isExpired ? 'text-red-500' : 'text-gray-800'}`}>
        {timeLeft}
      </span>
    );
  };

  const handleOfferExpiry = async (offerId: number) => {
    setLatestOfferDetail((prev: any) => ({ ...prev, status: "expired" }));

    const updateMessages = (prev: Message[]) =>
      prev.map(msg =>
        msg.messageType === "offer" &&
          msg.data?.offerDetails?.offer_id === offerId
          ? {
            ...msg,
            data: {
              ...msg.data,
              offerDetails: {
                ...msg.data.offerDetails,
                status: "expired"
              }
            }
          }
          : msg
      );

    setAllMessages(updateMessages);
    setDisplayedMessages(updateMessages);

    await axios.post(process.env.REACT_APP_API_URL + `/api/${localStorage.getItem("userRole").toLowerCase()}/trade/action/trade-update-offer-status`, {
      offer_id: offerId,
      trade_id: tradeDetail.id,
      trade_shortid: tradeDetail.shortid,
      ticket_id: tradeDetail.ticket_id,
      data: {
        offerId: offerId
      },
      status: "expired"
    });
  };

  const handleOpenConfirmOfferModal = () => {
    setIsOpenConfirmOfferModal(true);
  };

  const handleCloseConfirmOfferModal = () => {
    setIsOpenConfirmOfferModal(false);
  };

  const handleOpenAcceptOfferConfirmationModal = (msg: Message) => {
    setSelectedOfferDetail(msg.data.offerDetails);
    setIsOpenAcceptOfferConfirmationModal(true);
  };

  const handleCloseAcceptOfferConfirmationModal = () => {
    setSelectedOfferDetail(null);
    setIsOpenAcceptOfferConfirmationModal(false);
  };

  const handleOpenConfirmCloseTicketModal = () => {
    setIsOpenConfirmCloseTicketModal(true);
  };

  const handleCloseConfirmCloseTicketModal = () => {
    setIsOpenConfirmCloseTicketModal(false);
  };

  const getSubaccountBalances = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
        `/api/subaccount/get-subaccount-balance`
      );

      const balances = response.data?.balances;
      if (balances) {
        setSubaccountBalance(balances);
      }
    } catch (error) {
      console.error("Error get balance data", error);
      return false;
    }
  };

  return (
    <>
      {isOpenAcceptOfferConfirmationModal && userRole !== "superadmin" && selectedOfferDetail !== null &&
        <AcceptOfferConfirmationModal
          tradeDetail={tradeDetail}
          offerDetail={selectedOfferDetail}
          isOpen={isOpenAcceptOfferConfirmationModal}
          onClose={handleCloseAcceptOfferConfirmationModal}
          setSwalProps={setSwalProps}
        />
      }
      {userRole === "superadmin" && isOpenConfirmCloseTicketModal &&
        <CloseTicketConfirmationModal
          dataToProcess={tradeDetail}
          isOpen={isOpenConfirmCloseTicketModal}
          onClose={handleCloseConfirmCloseTicketModal}
          setSwalProps={setSwalProps}
        />
      }
      <SubmitOfferModal
        tradeDetail={tradeDetail}
        isOpen={isOpenConfirmOfferModal}
        onClose={handleCloseConfirmOfferModal}
        setSwalProps={setSwalProps}
      />
      <main className="flex flex-1 flex-col">
        {tradeDetail && !isChatLoading &&
          <>
            <div className="space-y-1 border-b border-gray-200 px-4 py-4 md:px-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="mt-1 text-base text-navy-700 font-bold">
                    {userRole === "superadmin" &&
                      <>{tradeDetail.subaccount.name} - {tradeDetail.from_asset}/{tradeDetail.to_asset} - <DisplayAmountWithDecimals amount={Number(tradeDetail?.from_amount)} currency={tradeDetail.from_asset} decimal={tradeDetail?.from_asset_decimal} /> {tradeDetail.from_asset}</>
                    }
                    {userRole !== "superadmin" &&
                      <>{tradeDetail.from_asset}/{tradeDetail.to_asset} - <DisplayAmountWithDecimals amount={Number(tradeDetail?.from_amount)} currency={tradeDetail.from_asset} decimal={tradeDetail?.from_asset_decimal} /> {tradeDetail.from_asset}</>
                    }
                  </h3>
                  <p className="text-xs text-navy-700">
                    Trade ID:<span className="ml-1 font-bold">#{tradeDetail.shortid}</span>
                    {/* Trade ID : <span className="ml-1 font-medium">#{tradeDetail.shortid}</span> |
                    Agent :<span className="ml-1 font-bold">Jenny</span> */}
                  </p>
                </div>

                {userRole === "superadmin" && tradeDetail.status === "awaiting" && !latestOfferDetail && !isChatLoading && displayedMessages.length > 0 &&
                  <Button
                    onClick={handleOpenConfirmOfferModal}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                    size="sm"
                  >
                    <GrTransaction /> Submit Offer
                  </Button>
                }

                {userRole === "superadmin" && tradeDetail.conversation_status === "open" && (tradeDetail.status === "completed" || tradeDetail.status === "rejected" || tradeDetail.status === "cancelled") &&
                  <Button
                    onClick={handleOpenConfirmCloseTicketModal}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                    size="sm"
                  >
                    Close Ticket
                  </Button>
                }
              </div>
            </div>
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto px-4 py-4 md:px-6 flex flex-col gap-2"
            >
              {isLoadingMore && (
                <div className="flex items-center justify-center py-4">
                  <ImSpinner8 className="spinner text-primary-500" style={{ fontSize: '1.5rem' }} />
                  <span className="ml-2 text-xs text-gray-500">Loading previous conversations...</span>
                </div>
              )}

              {hasMoreOlderMessages() && !isLoadingMore && (
                <div className="flex items-center justify-center py-2">
                  <Button
                    onClick={loadMoreMessages}
                    size="sm"
                    className="px-3 py-2 text-xs text-primary-600 border border-gray-300 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Show previous conversations
                  </Button>
                </div>
              )}

              {displayedMessages.map((msg, i) => {
                const isMine = msg.senderRole === userRole;
                const previousMsg = i > 0 ? displayedMessages[i - 1] : null;
                const showDateHeader = shouldShowDateHeader(msg, previousMsg, profileTimeZone);
                return (
                  <div key={i}>
                    {showDateHeader && (
                      <div className="flex items-center my-4">
                        <div className="flex-1 border-t border-gray-300"></div>
                        <span className="px-4 text-sm text-gray-500 bg-white">
                          {formatDateHeader(msg.date, profileTimeZone)}
                        </span>
                        <div className="flex-1 border-t border-gray-300"></div>
                      </div>
                    )}
                    <div
                      className={`w-full flex ${isMine ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[60%] flex flex-col ${isMine ? "items-end" : "items-start"}`}
                      >
                        <div className={`px-3 py-2 rounded text-sm break-words text-navy-700 ${isMine ? "bg-gray-100" : "bg-blue-100"} ${tradeDetail.from_network !== "fiat" ? "min-w-[300px]" : ""}`}>
                          {msg.messageType === "offer" ? (
                            (() => {
                              const parts = msg.text.split('.');
                              const introText = parts[0] + '.';
                              return (
                                <>
                                  <p className="text-sm mb-2">{introText}</p>
                                  <div className="bg-white rounded p-2 max-w-sm shadow-sm text-gray-800 mb-2">
                                    <div className="space-y-2">
                                      <div className="flex items-center">
                                        <span className="text-sm font-bold text-navy-700 mr-1">Trade #{msg.data?.offerDetails?.trade_shortid}</span>
                                      </div>

                                      <div className="flex items-center">
                                        <span className="text-sm font-bold text-navy-700 mr-1">Sell:</span>
                                        <span className="text-sm text-gray-800">
                                          <DisplayAmountWithDecimals
                                            amount={Number(msg.data?.offerDetails?.sell)}
                                            currency={msg.data.offerDetails?.sell_asset}
                                            decimal={tradeDetail?.from_asset_decimal}
                                          />
                                          &nbsp;{msg.data.offerDetails?.sell_asset}
                                        </span>
                                      </div>

                                      <div className="flex items-center">
                                        <span className="text-sm font-bold text-navy-700 mr-1">Receive:</span>
                                        <span className="text-sm text-gray-800">
                                          <DisplayAmountWithDecimals
                                            amount={Number(msg.data?.offerDetails?.receive)}
                                            currency={msg.data.offerDetails?.receive_asset}
                                            decimal={tradeDetail?.to_asset_decimal}
                                          />
                                          &nbsp;{msg.data.offerDetails?.receive_asset}
                                        </span>
                                      </div>

                                      <div className="flex items-center">
                                        <span className="text-sm font-bold text-navy-700 mr-1">Rate:</span>
                                        <span className="text-sm text-gray-800">
                                          <DisplayAmountWithDecimals
                                            amount={Number(msg.data?.offerDetails?.rate)}
                                            currency={msg.data.offerDetails?.sell_asset}
                                            decimal={tradeDetail?.from_asset_decimal}
                                          />
                                          &nbsp;{msg.data.offerDetails?.sell_asset}
                                        </span>
                                      </div>

                                      {tradeDetail.from_network === "fiat" &&
                                        <div className="flex items-start">
                                          <span className="text-sm font-bold text-navy-700 mr-1">Settlement Address:</span>
                                          <span className="text-sm text-gray-800 font-mono text-right break-all">{msg.data?.offerDetails?.settlement_address.slice(0, 9)}...{msg.data?.offerDetails?.settlement_address.slice(-4)}</span>
                                        </div>
                                      }

                                      {msg.data?.offerDetails?.status === "pending" &&
                                        <div className="flex items-start">
                                          <span className="text-sm font-bold text-navy-700 mr-1">Expires in:</span>
                                          <CountdownTimer
                                            expiryTimestamp={msg.data?.offerDetails?.expired}
                                            onExpire={handleOfferExpiry}
                                            offerId={msg.data?.offerDetails?.offer_id}
                                          />
                                        </div>
                                      }
                                    </div>
                                  </div>
                                  {msg.data?.offerDetails?.status === "pending" &&
                                    <>
                                      {userRole === "superadmin" &&
                                        <div className="w-full status-pending py-2 px-4 rounded transition-colors max-w-sm text-center">
                                          Pending Acceptance
                                        </div>
                                      }
                                      {userRole !== "superadmin" &&
                                        <button onClick={() => handleOpenAcceptOfferConfirmationModal(msg)} className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded transition-colors max-w-sm">
                                          Accept Trade
                                        </button>
                                      }
                                    </>
                                  }
                                  {msg.data?.offerDetails?.status === "accepted" &&
                                    <div className="w-full status-accepted py-2 px-4 rounded transition-colors max-w-sm text-center">
                                      Accepted
                                    </div>
                                  }
                                  {msg.data?.offerDetails?.status === "cancelled" &&
                                    <div className="w-full bg-gray-200 text-navy-700 py-2 px-4 rounded transition-colors max-w-sm text-center">
                                      Cancelled
                                    </div>
                                  }
                                  {msg.data?.offerDetails?.status === "expired" &&
                                    <div className="w-full bg-amber-100 text-amber-500 py-2 px-4 rounded transition-colors max-w-sm text-center">
                                      Expired
                                    </div>
                                  }
                                </>
                              );
                            })()
                          ) : (
                            <>{msg.text}</>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 mt-1 px-1">
                          {formatTime(msg.date, profileTimeZone)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div className="border-t border-gray-200 px-4 py-4 md:p-4">
              {tradeDetail?.conversation_status === 'closed' ? (
                <div className="flex items-center justify-center py-4">
                  <div className="text-center text-gray-600">
                    <p className="text-sm font-medium">This ticket has been closed</p>
                    <p className="text-xs text-gray-500 mt-1">No further messages can be sent</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <input
                    disabled={isReconnecting || sendingMessage}
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    placeholder="Write your message"
                    className="focus:border-primary-500 h-10 flex-1 rounded-full bg-[#F4F7FE] px-4 py-2 text-sm placeholder-gray-400 focus:outline-none"
                  />
                  <Button
                    isDisabled={!newMessage.trim() || sendingMessage}
                    onClick={() => sendMessage()}
                    className="rounded-full bg-primary-500 p-3 text-white shadow transition hover:bg-primary-700"
                  >
                    {isReconnecting &&
                      <ImSpinner8 className="spinner text-white" style={{ fontSize: '1rem' }} />
                    }
                    {!isReconnecting &&
                      <FiSend />
                    }
                  </Button>
                </div>
              )}
            </div>
          </>
        }
        {!tradeDetail && !isChatLoading &&
          <div className="flex items-center justify-center h-full text-gray-600">
            <div>Click on a ticket to view the conversation</div>
          </div>
        }
        {isChatLoading && displayedMessages.length === 0 &&
          <div className="flex items-center justify-center h-full">
            <ImSpinner8 className="spinner text-primary-500" style={{ fontSize: '2.2rem' }} />
          </div>
        }
      </main>
    </>
  );
};

export default TicketChatBox;